/**
 * Selection-aware clipboard helpers for [data-copy] buttons and Editor.js.
 *
 * When copying from an editor/offcanvas text panel: use the current selection
 * if present, otherwise the full data-copy payload. Editor.js multi-block
 * selections only contribute text from textual surfaces (paragraph, header,
 * quote, box, list) — media/embed chrome is skipped.
 */

/** CSS selector for Editor.js surfaces that hold copyable body text. */
export const EDITOR_TEXTUAL_SURFACE_SELECTOR = [
	'.ce-paragraph',
	'.ce-header',
	'.cdx-quote__text',
	'.cdx-quote__caption',
	'.cdx-box__text',
	'.cdx-list__item-content',
	'.cdx-list__item',
].join(', ');

/**
 * @param {Element} el
 * @returns {boolean}
 */
function hasClass(el, className) {
	return (el.className || '').split(/\s+/).includes(className);
}

/**
 * Keep list leaf/content nodes only so nested items do not duplicate parent text.
 *
 * @param {Element} el
 * @returns {boolean}
 */
function isCopyableTextualSurface(el) {
	if (!hasClass(el, 'cdx-list__item')) {
		return true;
	}

	// Prefer dedicated content nodes when the Nested List markup provides them.
	if (typeof el.querySelector === 'function' && el.querySelector('.cdx-list__item-content')) {
		return false;
	}

	// Skip non-leaf items: textContent would include nested children twice.
	if (typeof el.querySelector === 'function' && el.querySelector('.cdx-list__item')) {
		return false;
	}

	return true;
}

/**
 * Plain text of an Editor.js block using only textual surfaces.
 *
 * @param {Element|null|undefined} blockEl
 * @param {string} [surfaceSelector]
 * @returns {string}
 */
export function extractTextualBlockText(blockEl, surfaceSelector = EDITOR_TEXTUAL_SURFACE_SELECTOR) {
	if (!blockEl || typeof blockEl.querySelectorAll !== 'function') {
		return '';
	}

	return Array.from(blockEl.querySelectorAll(surfaceSelector))
		.filter(isCopyableTextualSurface)
		.map((el) => (el.innerText || el.textContent || '').trim())
		.filter(Boolean)
		.join('\n');
}

/**
 * @param {Iterable<Element>|ArrayLike<Element>|null|undefined} selectedBlocks
 * @returns {string[]}
 */
export function extractParagraphsFromSelectedBlocks(selectedBlocks) {
	if (!selectedBlocks) {
		return [];
	}

	return Array.from(selectedBlocks)
		.map((block) => extractTextualBlockText(block))
		.filter(Boolean);
}

/**
 * Text of a Range clipped to an element (intersection), or empty when disjoint.
 *
 * @param {Range} range
 * @param {Element} element
 * @returns {string}
 */
function getRangeTextWithinElement(range, element) {
	if (!range || !element || typeof document === 'undefined' || typeof document.createRange !== 'function') {
		return '';
	}

	try {
		if (typeof range.intersectsNode === 'function' && !range.intersectsNode(element)) {
			return '';
		}

		const elRange = document.createRange();
		elRange.selectNodeContents(element);

		const intersection = range.cloneRange();
		if (range.compareBoundaryPoints(Range.START_TO_START, elRange) < 0) {
			intersection.setStart(elRange.startContainer, elRange.startOffset);
		}
		if (range.compareBoundaryPoints(Range.END_TO_END, elRange) > 0) {
			intersection.setEnd(elRange.endContainer, elRange.endOffset);
		}

		return (intersection.toString() || '').trim();
	} catch (_e) {
		return (element.innerText || element.textContent || '').trim();
	}
}

/**
 * Find the content root related to a copy button (offcanvas / editor actbar).
 *
 * @param {Element} buttonEl
 * @returns {Element|null}
 */
export function findCopyContentRoot(buttonEl) {
	if (!buttonEl || typeof buttonEl.closest !== 'function') {
		return null;
	}

	// Prefer the nearest panel so the translation copy button does not resolve
	// to the original #editorjs that also lives under .offcanvas-detail-text.
	const panel =
		buttonEl.closest('.offcanvas-translation-container') ||
		buttonEl.closest('.offcanvas-detail-text') ||
		buttonEl.closest('.flex-one') ||
		buttonEl.closest('.editor-actbar')?.parentElement ||
		null;

	if (panel && typeof panel.querySelector === 'function') {
		const scoped = panel.querySelector('#editorjs, #translate-editorjs, .codex-editor__redactor, .codex-editor');
		if (scoped) {
			return scoped;
		}
	}

	if (buttonEl.closest('.editor-actbar') && typeof document !== 'undefined') {
		return document.getElementById('editorjs');
	}

	return null;
}

/**
 * Extract selected text inside `root`, keeping only textual Editor.js surfaces
 * when the root is an Editor.js document. Plain HTML panels (agency offcanvas)
 * use the native selection string.
 *
 * @param {Element} root
 * @param {Selection|null|undefined} selection
 * @returns {string}
 */
export function extractSelectedTextualText(root, selection) {
	if (!root) {
		return '';
	}

	const hasEditorBlocks = typeof root.querySelector === 'function' && Boolean(root.querySelector('.ce-block'));

	// Editor.js whole-block selection (.ce-block--selected clears the native range).
	const selectedBlocks =
		typeof root.querySelectorAll === 'function' ? root.querySelectorAll('.ce-block--selected') : [];
	if (selectedBlocks.length) {
		return extractParagraphsFromSelectedBlocks(selectedBlocks).join('\n\n');
	}

	if (!selection || selection.isCollapsed || !selection.rangeCount) {
		return '';
	}

	const anchor = selection.anchorNode;
	const focus = selection.focusNode;
	if (!anchor || !focus || !root.contains(anchor) || !root.contains(focus)) {
		return '';
	}

	// Plain offcanvas / non-Editor.js content: native selection is enough.
	if (!hasEditorBlocks) {
		return (selection.toString() || '').trim();
	}

	const range = selection.getRangeAt(0);
	const surfaces = Array.from(root.querySelectorAll(EDITOR_TEXTUAL_SURFACE_SELECTOR)).filter((el) => {
		try {
			return typeof range.intersectsNode !== 'function' || range.intersectsNode(el);
		} catch (_e) {
			return false;
		}
	});

	if (!surfaces.length) {
		return '';
	}

	return surfaces
		.map((el) => getRangeTextWithinElement(range, el))
		.filter(Boolean)
		.join('\n\n');
}

/**
 * Resolve the string to copy for a [data-copy] control.
 *
 * @param {Element} buttonEl
 * @param {{ getSelectedText?: () => string, selection?: Selection|null }|Selection|null} [options]
 * @returns {string|null}
 */
export function resolveDataCopyContent(buttonEl, options = null) {
	const fallback = buttonEl?.getAttribute?.('data-copy');
	if (fallback == null || fallback === '') {
		return null;
	}

	let selected = '';

	if (options && typeof options.getSelectedText === 'function') {
		selected = (options.getSelectedText() || '').trim();
	} else {
		const selection =
			options && typeof options.rangeCount === 'number'
				? options
				: (options?.selection ?? (typeof window !== 'undefined' ? window.getSelection() : null));
		const root = findCopyContentRoot(buttonEl);
		if (root) {
			selected = extractSelectedTextualText(root, selection);
		}
	}

	if (selected) {
		return selected;
	}

	return fallback;
}

/**
 * Full plain text of an editor/offcanvas root using only textual surfaces when
 * Editor.js blocks are present; otherwise the root's visible text.
 *
 * @param {Element|null|undefined} root
 * @returns {string}
 */
export function extractAllTextualText(root) {
	if (!root) {
		return '';
	}

	if (typeof root.querySelector === 'function' && root.querySelector('.ce-block')) {
		return Array.from(root.querySelectorAll('.ce-block'))
			.map((block) => extractTextualBlockText(block))
			.filter(Boolean)
			.join('\n\n');
	}

	return (root.innerText || root.textContent || '').trim();
}

/**
 * Paragraph list for Editor.js translate toolbar (selection-aware, textual only).
 *
 * @param {Element|null|undefined} root
 * @param {Selection|null|undefined} selection
 * @returns {string[]}
 */
export function getEditorSelectionParagraphs(root, selection) {
	const text = extractSelectedTextualText(root, selection);
	if (!text) {
		return [];
	}

	return text
		.split(/\n+/)
		.map((s) => s.trim())
		.filter(Boolean);
}
